import {
  AUTH_REFRESH_PATH,
  AUTH_SESSION_PATH,
} from '../api/constants.js';

import { TOKEN_EXPIRY_GRACE_MS } from './token.js';

const HEALTH_CHECK_TTL = 30 * 1000;
const UNHEALTHY_COOLDOWN = 5 * 1000;

const EXPIRATION_KEYS = [
  'token_expires_at',
  'expires_at',
  'expiration',
  'expires',
  'exp',
];

let sessionHealth = 'unknown';
let validUntil = 0;
let inFlightHealthCheck = null;
let recoveryPromise = null;
let refreshSupported = true;
let sessionFailure = null;
let hasAttemptedRefresh = false;
let lastSessionHealthReason = null;
let lastSessionHealthReasonType = null;

const now = () => Date.now();

const normalizeExpiresValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1e12 ? value : value * 1000;
  }
  if (typeof value === 'string') {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      return numericValue > 1e12 ? numericValue : numericValue * 1000;
    }
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
    return null;
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  return null;
};

const computeExpiryTarget = (expiresAtMs, graceMs) => {
  const grace = Number.isFinite(graceMs) && graceMs >= 0 ? graceMs : TOKEN_EXPIRY_GRACE_MS;
  if (!Number.isFinite(expiresAtMs)) return null;
  const candidate = expiresAtMs - grace;
  if (!Number.isFinite(candidate)) return null;
  if (candidate <= now()) {
    return null;
  }
  return candidate;
};

const isFetchNetworkError = (error) => {
  if (!error) return false;
  const message =
    typeof error === 'string'
      ? error
      : typeof error.message === 'string'
        ? error.message
        : '';
  if (!message) return false;
  const normalized = message.toLowerCase();
  return (
    normalized.includes('failed to fetch') ||
    normalized.includes('networkerror') ||
    normalized.includes('network error')
  );
};

export const SESSION_HEALTH_EVENT = 'bluezap:session-health';

const resolveReasonType = (reason) => {
  if (!reason) return null;
  if (isFetchNetworkError(reason)) return 'network';

  const status = reason?.status ?? reason?.response?.status;
  if (status === 401 || status === 403) return 'auth';

  if (typeof reason === 'string') {
    const normalized = reason.toLowerCase();
    if (normalized.includes('auth 401') || normalized.includes('auth 403')) {
      return 'auth';
    }
    if (normalized.includes('unauthorized') || normalized.includes('forbidden')) {
      return 'auth';
    }
  }

  return 'unknown';
};

const emitSessionHealthEvent = () => {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent(SESSION_HEALTH_EVENT, {
        detail: {
          sessionHealth,
          validUntil,
          reason: lastSessionHealthReason,
          reasonType: lastSessionHealthReasonType,
        },
      })
    );
  } catch {
    // ignore when window is unavailable
  }
};

const updateSessionHealthSnapshot = (reason = null) => {
  lastSessionHealthReason = reason ?? null;
  lastSessionHealthReasonType = resolveReasonType(lastSessionHealthReason);
  emitSessionHealthEvent();
};

export function setSessionExpiryFromToken(expiresAtMs, { graceMs } = {}) {
  const target = computeExpiryTarget(expiresAtMs, graceMs);
  if (target === null) {
    markUnhealthy(UNHEALTHY_COOLDOWN, 'expiração local');
    return false;
  }
  sessionHealth = 'healthy';
  validUntil = target;
  clearSessionFailure();
  updateSessionHealthSnapshot(null);
  return true;
}

const lockSessionFailure = (result) => {
  if (result) {
    sessionFailure = result;
  }
};

const clearSessionFailure = () => {
  sessionFailure = null;
  hasAttemptedRefresh = false;
};

const markHealthy = (ttl = HEALTH_CHECK_TTL) => {
  sessionHealth = 'healthy';
  validUntil = now() + ttl;
  clearSessionFailure();
  updateSessionHealthSnapshot(null);
};

const markUnhealthy = (cooldown = UNHEALTHY_COOLDOWN, reason = null) => {
  sessionHealth = 'unhealthy';
  validUntil = now() + cooldown;
  updateSessionHealthSnapshot(reason);
};

const buildHealthResult = (allowed, status, reason) => ({
  allowed,
  status,
  reason,
});

const runHealthCheck = async () => {
  try {
    const response = await fetch(AUTH_SESSION_PATH, {
      method: 'GET',
      credentials: 'include',
    });

    if (response.ok) {
      markHealthy();
      return buildHealthResult(true, sessionHealth);
    }

    if (response.status === 401 || response.status === 403) {
      markUnhealthy(UNHEALTHY_COOLDOWN, `Auth ${response.status}`);
      return buildHealthResult(false, sessionHealth, `Auth ${response.status}`);
    }

    console.warn(
      'Session health check retornou status inesperado',
      response.status,
      response.statusText
    );
    markHealthy();
    return buildHealthResult(true, sessionHealth, `Status ${response.status}`);
  } catch (error) {
    console.warn('Falha ao validar sessão:', error);
    markUnhealthy(UNHEALTHY_COOLDOWN, error);
    return buildHealthResult(false, sessionHealth, error);
  }
};

const extractExpirationFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  for (const key of EXPIRATION_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(payload, key)) {
      continue;
    }
    const normalized = normalizeExpiresValue(payload[key]);
    if (Number.isFinite(normalized)) {
      return normalized;
    }
  }
  return null;
};

const extractExpirationFromResponse = async (response) => {
  if (!response || typeof response.json !== 'function') {
    return null;
  }
  try {
    const payload = await response.json();
    return extractExpirationFromPayload(payload);
  } catch {
    return null;
  }
};

export async function ensureSessionHealthy() {
  if (recoveryPromise) {
    try {
      await recoveryPromise;
    } catch {
      // ignore; failure will be handled by sessionFailure
    }
  }

  if (sessionFailure) {
    return sessionFailure;
  }

  if (sessionHealth === 'healthy' && now() < validUntil) {
    return buildHealthResult(true, sessionHealth);
  }

  if (inFlightHealthCheck) {
    return inFlightHealthCheck;
  }

  inFlightHealthCheck = runHealthCheck();
  try {
    const result = await inFlightHealthCheck;
    if (!result.allowed) {
      const isNetworkIssue = isFetchNetworkError(result.reason);
      if (!isNetworkIssue) {
        lockSessionFailure(result);
      }
    }
    return result;
  } finally {
    inFlightHealthCheck = null;
  }
}

const tryRefreshSession = async () => {
  if (!refreshSupported) {
    const failureResult = buildHealthResult(false, sessionHealth, 'refresh falhou');
    lockSessionFailure(failureResult);
    return { recovered: false, reason: 'refresh falhou' };
  }

  try {
    const response = await fetch(AUTH_REFRESH_PATH, {
      method: 'POST',
      credentials: 'include',
    });

    if (response.ok) {
      const expiresAt = await extractExpirationFromResponse(response);
      const refreshed = expiresAt ? setSessionExpiryFromToken(expiresAt) : false;
      if (!refreshed) {
        markHealthy();
      }
      return { recovered: true };
    }

    const status = response.status;
    if (status === 404 || status === 405) {
      refreshSupported = false;
      console.info('Refresh token endpoint não está disponível', status);
    } else {
      console.warn('Refresh token falhou', status, response.statusText);
    }

    const refreshReason =
      status === 401 || status === 403 ? `Auth ${status}` : 'refresh falhou';
    markUnhealthy(UNHEALTHY_COOLDOWN, refreshReason);
    const failureResult = buildHealthResult(false, sessionHealth, 'refresh falhou');
    lockSessionFailure(failureResult);
    return { recovered: false, status, reason: 'refresh falhou' };
  } catch (error) {
    console.warn('Falha ao tentar refresh de sessão:', error);
    markUnhealthy(UNHEALTHY_COOLDOWN, error);
    const networkError = isFetchNetworkError(error);
    const failureReason = networkError ? error : 'refresh falhou';
    const failureResult = buildHealthResult(false, sessionHealth, failureReason);
    if (!networkError) {
      lockSessionFailure(failureResult);
    }
    return {
      recovered: false,
      reason: networkError ? 'erro de rede' : 'refresh falhou',
      error,
    };
  }
};

export async function handleUnauthorizedResponse(status = 401) {
  const normalizedStatus = status === 403 ? 403 : 401;
  markUnhealthy(UNHEALTHY_COOLDOWN, `Auth ${normalizedStatus}`);

  if (recoveryPromise) {
    return recoveryPromise;
  }

  if (hasAttemptedRefresh) {
    const failureResult = buildHealthResult(false, sessionHealth, 'refresh falhou');
    lockSessionFailure(failureResult);
    return { recovered: false, reason: 'refresh falhou' };
  }

  hasAttemptedRefresh = true;
  recoveryPromise = tryRefreshSession();

  try {
    const result = await recoveryPromise;
    if (result?.recovered || result?.reason === 'erro de rede') {
      hasAttemptedRefresh = false;
    }
    return result;
  } finally {
    recoveryPromise = null;
  }
}

export function resetSessionHealth() {
  sessionHealth = 'unknown';
  validUntil = 0;
  inFlightHealthCheck = null;
  clearSessionFailure();
  updateSessionHealthSnapshot(null);
}

export function getSessionHealthSnapshot() {
  return {
    sessionHealth,
    validUntil,
    isHealthCheckInFlight: Boolean(inFlightHealthCheck),
    reason: lastSessionHealthReason,
    reasonType: lastSessionHealthReasonType,
  };
}
